import type { Disposable } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { createApp } from 'vue';
import OnboardingInputExampleGuide from '../../components/OnboardingInputExampleGuide.vue';
import type {
    OnboardingInputExampleGuidePresenter,
    OnboardingInputExampleGuideShowOptions,
} from '../onboarding-input-example-guide-presenter';

/** 单次演示 Vue 子应用的宿主样式类。 */
const GUIDE_HOST_CLASS = 'onboarding-input-example-guide-host';
/** 输入框内部用于定位打字浮层的临时样式类。 */
const GUIDE_INPUT_TARGET_CLASS = 'onboarding-input-example-guide-input-target';
/** 第一条示例用于定位手势的临时样式类。 */
const GUIDE_EXAMPLE_TARGET_CLASS = 'onboarding-input-example-guide-example-target';
/** 中日韩示例在关键词后继续展示的上下文字符数。 */
const CJK_KEYWORD_CONTEXT_LENGTH = 10;
/** 未匹配关键词时，中日韩示例默认展示的字符数。 */
const CJK_FALLBACK_LENGTH = 18;
/** 未匹配关键词时，拉丁语系示例默认展示的字符数。 */
const LATIN_FALLBACK_LENGTH = 42;

/** 判断文案是否主要使用中日韩字符。 */
function containsCjkText(text: string): boolean {
    return /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(text);
}

/** 截取指定数量的 Unicode 字符并移除结尾空白。 */
function sliceCharacters(text: string, length: number): string {
    return Array.from(text).slice(0, length).join('').trimEnd();
}

/** 从各语言的首条示例中提取长度适中且语义连续的打字演示文案。 */
function getTypingPreviewText(exampleText: string): string {
    const keywordPatterns = [
        /人形机器人/u,
        /人形機器人/u,
        /ヒューマノイド(?:・)?ロボット/u,
        /humanoid robot/iu,
    ];
    for (const pattern of keywordPatterns) {
        const match = exampleText.match(pattern);
        if (!match?.[0] || match.index === undefined) continue;
        const keywordEnd = match.index + match[0].length;
        if (!containsCjkText(match[0])) return exampleText.slice(0, keywordEnd).trimEnd();

        const keywordCharacterCount = Array.from(exampleText.slice(0, keywordEnd)).length;
        return sliceCharacters(
            exampleText,
            keywordCharacterCount + CJK_KEYWORD_CONTEXT_LENGTH,
        );
    }
    return sliceCharacters(
        exampleText,
        containsCjkText(exampleText) ? CJK_FALLBACK_LENGTH : LATIN_FALLBACK_LENGTH,
    );
}

/** 公共输入区与示例视觉演示挂载器实现。 */
@injectable()
export class OnboardingInputExampleGuidePresenterImpl implements OnboardingInputExampleGuidePresenter {
    /** 挂载不修改业务状态的输入与点击演示。 */
    // eslint-disable-next-line complexity, max-lines-per-function
    show(options: OnboardingInputExampleGuideShowOptions): Disposable {
        const groupTarget = options.groupElement;
        const exampleTarget = options.exampleElement;
        const inputInteractionTarget = options.inputElement;
        const inputTarget = options.inputElement?.querySelector<HTMLElement>('.agent-textarea');
        if (
            !(groupTarget instanceof HTMLElement)
            || !(exampleTarget instanceof HTMLElement)
            || (options.mode === 'typing-and-gesture'
                && (!(inputInteractionTarget instanceof HTMLElement) || !inputTarget))
        ) {
            throw new Error('Onboarding input example guide target is missing');
        }

        const host = document.createElement('span');
        host.className = GUIDE_HOST_CLASS;
        host.setAttribute('aria-hidden', 'true');
        groupTarget.appendChild(host);
        inputTarget?.classList.add(GUIDE_INPUT_TARGET_CLASS);
        exampleTarget.classList.add(GUIDE_EXAMPLE_TARGET_CLASS);

        const exampleText = getTypingPreviewText(
            exampleTarget.textContent?.replace(/\s+/g, ' ').trim() ?? '',
        );
        const app = createApp(OnboardingInputExampleGuide, {
            exampleTarget,
            exampleText,
            inputTarget,
            mode: options.mode,
        });
        try {
            app.mount(host);
        } catch (error) {
            inputTarget?.classList.remove(GUIDE_INPUT_TARGET_CLASS);
            exampleTarget.classList.remove(GUIDE_EXAMPLE_TARGET_CLASS);
            host.remove();
            throw error;
        }

        let isDisposed = false;
        const dispose = () => {
            if (isDisposed) return;
            isDisposed = true;
            inputInteractionTarget?.removeEventListener('pointerdown', dispose);
            inputInteractionTarget?.removeEventListener('focusin', dispose);
            app.unmount();
            host.remove();
            inputTarget?.classList.remove(GUIDE_INPUT_TARGET_CLASS);
            exampleTarget.classList.remove(GUIDE_EXAMPLE_TARGET_CLASS);
        };
        inputInteractionTarget?.addEventListener('pointerdown', dispose);
        inputInteractionTarget?.addEventListener('focusin', dispose);
        return { dispose };
    }
}
